import {
  ChatMessage,
  Commercial,
  CommercialService,
  Edit,
  EditReview,
  EditReviewAnalysis,
  EditReviewService,
  EditReviewStatus,
  PerformerContract,
  RolePerformerType,
  TEditReviewPerformerType,
} from '@talon/web/lib/api';
import { env } from '@talon/web/src/config/env';
import { useCommercialStore, usePerformerStore, useUserStore } from '@talon/web/src/context';
import {
  Accordion,
  AccordionPanel,
  Box,
  BoxProps,
  Button,
  CardBody,
  CardHeader,
  Heading,
  Image,
  Layer,
  Spinner,
  SpinnerProps,
  Text,
} from 'grommet';
import { Alert, Checkmark, Magic, StatusWarning, Update } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import useWebSocket from 'react-use-websocket';
import { ModalCard } from '../../ModalCard';
import { ContractedPerformersPanel } from '../EditReviewModal/ContractedPerformersPanel';
import { EnlargedFrameModal } from '../EditReviewModal/EnlargedFrameModal';
import { MergeDialogModal } from '../EditReviewModal/MergeDialogModal';
import { UnknownPerformersPanel } from '../EditReviewModal/UnknownPerformersPanel';
import { StatusPill, StatusTone } from '../../StatusPill';

export interface ICommercialEditReviewModalProps {
  commercial: Commercial;
  setShowCommercialEditReviewModal: (isVisible: boolean) => void;
  initialEditId?: string;
}

enum Verdict {
  upgrade = 'Upgrade',
  downgrade = 'Downgrade',
  noChange = 'No Change',
  outgraded = 'Manually Outgraded',
}

type StatsData = { principals: number; extras: number; unknown: number };
type WarningsData = { outgraded: number; invalid: number; needsId: number };

type EditReviewData = {
  edit: Edit;
  editReview: EditReview | null;
  loading: boolean;
  contracts: PerformerContract[];
  knownAnalyses: {
    contract: PerformerContract;
    analysis?: EditReviewAnalysis;
    frameUrls?: string[];
    verdict: Verdict;
  }[];
  unknownAnalyses: {
    analysis: EditReviewAnalysis;
    frameUrls?: string[];
  }[];
  processingAction: string | null;
  selectedPerformers: Set<string>;
  showMergeDialog: boolean;
  mergeName: string;
  enlargedFrameUrl: string | null;
};

export const CommercialEditReviewModal = observer(
  ({ commercial, setShowCommercialEditReviewModal, initialEditId }: ICommercialEditReviewModalProps) => {
    /** Context **/
    const commercialStore = useCommercialStore();
    const performerStore = usePerformerStore();
    const userStore = useUserStore();

    /** State **/
    const [editReviewsData, setEditReviewsData] = useState<Map<string, EditReviewData>>(new Map());
    const [batchProgress, setBatchProgress] = useState<{ current: number; total: number } | null>(null);
    const [initialLoading, setInitialLoading] = useState(true);
    const [activeAccordionIndexes, setActiveAccordionIndexes] = useState<number[]>([]);

    /** Computed **/

    // WebSocket for real-time EditReview completion updates
    const { lastJsonMessage } = useWebSocket(`${env.apiWsHost}:${env.apiPort}`, {
      protocols: ['Authorization', userStore.token],
      shouldReconnect: () => true,
      reconnectInterval: 3000,
      share: true, // Share connection with other components
    });

    /** Methods **/

    // Process analyses for an edit review - memoized to avoid recreating on every render
    const processEditAnalyses = useCallback(
      (
        editReview: EditReview | null,
        contracts: PerformerContract[]
      ): {
        knownAnalyses: {
          contract: PerformerContract;
          analysis?: EditReviewAnalysis;
          frameUrls?: string[];
          verdict: Verdict;
        }[];
        unknownAnalyses: {
          analysis: EditReviewAnalysis;
          frameUrls?: string[];
        }[];
      } => {
        if (!editReview || contracts.length === 0) {
          return { knownAnalyses: [], unknownAnalyses: [] };
        }

        const knownPerformerIds = new Set(contracts.map((c) => c.performer.id));

        const known = contracts.map((contract) => {
          const analysis = editReview.analyses?.find((a) => a.performerId === contract.performer.id);

          if (analysis?.outgraded) {
            return { contract, analysis, frameUrls: [], verdict: Verdict.outgraded };
          }

          if (!analysis || analysis.frameAppears.length === 0) {
            if (contract.role.performer_type === RolePerformerType.Extra) {
              return { contract, analysis, frameUrls: [], verdict: Verdict.noChange };
            } else if (contract.role.performer_type === RolePerformerType.OnCameraPrincipal) {
              return { contract, analysis, frameUrls: [], verdict: Verdict.downgrade };
            } else {
              return { contract, analysis, frameUrls: [], verdict: Verdict.noChange };
            }
          }

          const frameUrls = editReview.framePaths
            ?.filter((imageLocation) =>
              analysis.frameAppears.some((frameIndex) =>
                imageLocation.fileName.includes(`${String(frameIndex).padStart(4, '0')}.jpg`)
              )
            )
            .map((imageLocation) => imageLocation.signedUrl);

          if (contract.role.performer_type === RolePerformerType.Extra) {
            if (analysis.type === RolePerformerType.Extra) {
              return { contract, analysis, frameUrls, verdict: Verdict.noChange };
            } else if (analysis.type === RolePerformerType.OnCameraPrincipal) {
              return { contract, analysis, frameUrls, verdict: Verdict.upgrade };
            } else {
              return { contract, analysis, frameUrls, verdict: Verdict.noChange };
            }
          } else if (contract.role.performer_type === RolePerformerType.OnCameraPrincipal) {
            if (analysis.type === RolePerformerType.OnCameraPrincipal) {
              return { contract, analysis, frameUrls, verdict: Verdict.noChange };
            } else if (analysis.type === RolePerformerType.Extra) {
              return { contract, analysis, frameUrls, verdict: Verdict.downgrade };
            } else {
              return { contract, analysis, frameUrls, verdict: Verdict.noChange };
            }
          } else {
            return { contract, analysis, frameUrls, verdict: Verdict.noChange };
          }
        });

        const unknown = (editReview.analyses || [])
          .filter((analysis) => !knownPerformerIds.has(analysis.performerId))
          .map((analysis) => {
            const frameUrls = editReview.framePaths
              ?.filter((imageLocation) =>
                analysis.frameAppears.some((frameIndex) =>
                  imageLocation.fileName.includes(`${String(frameIndex).padStart(4, '0')}.jpg`)
                )
              )
              .map((imageLocation) => imageLocation.signedUrl);

            return { analysis, frameUrls };
          });

        return { knownAnalyses: known, unknownAnalyses: unknown };
      },
      []
    );

    const handleRunReview = async (editId: string) => {
      const dataMap = new Map(editReviewsData);
      const editData = dataMap.get(editId);

      // Validate that edit has required data
      if (editData && !editData.edit.asset_id) {
        toast.error('Cannot run review: This edit has no video asset attached');
        return;
      }

      if (editData) {
        dataMap.set(editId, { ...editData, loading: true });
        setEditReviewsData(dataMap);
      }

      try {
        const response = await commercialStore.createEditReview(editId);
        if (response?.editReview) {
          const updatedMap = new Map(editReviewsData);
          const existing = updatedMap.get(editId);
          if (existing) {
            const { knownAnalyses, unknownAnalyses } = processEditAnalyses(response.editReview, existing.contracts);
            updatedMap.set(editId, {
              ...existing,
              editReview: response.editReview,
              loading: false,
              knownAnalyses,
              unknownAnalyses,
            });
            setEditReviewsData(updatedMap);
          }
          toast.success('Edit review started');
        }
      } catch (error: unknown) {
        console.error('Error running edit review:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to start edit review';

        // Show more specific error messages
        if (errorMessage.includes('missing required data')) {
          toast.error('Cannot run review: Edit is missing required video asset');
        } else {
          toast.error(errorMessage);
        }

        const updatedMap = new Map(editReviewsData);
        const existing = updatedMap.get(editId);
        if (existing) {
          updatedMap.set(editId, { ...existing, loading: false });
          setEditReviewsData(updatedMap);
        }
      }
    };

    const handleRunAllReviews = async () => {
      // Filter edits that need review and have assets
      const editsToRun = Array.from(editReviewsData.values()).filter(
        (data) =>
          data.edit.asset_id && // Only include edits with assets
          (!data.editReview || data.editReview.status === EditReviewStatus.FAILED)
      );

      const editsWithoutAssets = Array.from(editReviewsData.values()).filter((data) => !data.edit.asset_id);

      if (editsToRun.length === 0) {
        if (editsWithoutAssets.length > 0) {
          toast.info(`No edits ready to review. ${editsWithoutAssets.length} edit(s) missing video assets.`);
        } else {
          toast.info('All edit reviews are already complete');
        }
        return;
      }

      if (editsWithoutAssets.length > 0) {
        toast.info(
          `Running ${editsToRun.length} review(s). Skipping ${editsWithoutAssets.length} edit(s) without assets.`
        );
      }

      setBatchProgress({ current: 0, total: editsToRun.length });

      const promises = editsToRun.map(async (data, index) => {
        try {
          const response = await commercialStore.createEditReview(data.edit.id);
          setBatchProgress({ current: index + 1, total: editsToRun.length });
          return { editId: data.edit.id, editReview: response?.editReview || null };
        } catch (error) {
          console.error(`Error running edit review for ${data.edit.id}:`, error);
          return { editId: data.edit.id, editReview: null };
        }
      });

      const results = await Promise.all(promises);

      const updatedMap = new Map(editReviewsData);
      results.forEach(({ editId, editReview }) => {
        const existing = updatedMap.get(editId);
        if (existing && editReview) {
          const { knownAnalyses, unknownAnalyses } = processEditAnalyses(editReview, existing.contracts);
          updatedMap.set(editId, {
            ...existing,
            editReview,
            loading: false,
            knownAnalyses,
            unknownAnalyses,
          });
        }
      });

      setEditReviewsData(updatedMap);
      setBatchProgress(null);
      toast.success(`Started ${editsToRun.length} edit review${editsToRun.length > 1 ? 's' : ''}`);
    };

    // Action handlers for performers
    const handleReclassify = async (
      editId: string,
      performerId: string,
      newType: RolePerformerType,
      reason?: string
    ) => {
      const editData = editReviewsData.get(editId);
      if (!editData) return;

      setEditReviewsData(new Map(editReviewsData).set(editId, { ...editData, processingAction: performerId }));

      try {
        const response = await EditReviewService.reclassifyPerformer(editId, {
          performerId,
          newType: newType as unknown as TEditReviewPerformerType,
          reason,
        });

        if (response?.editReview) {
          const { knownAnalyses, unknownAnalyses } = processEditAnalyses(response.editReview, editData.contracts);
          setEditReviewsData(
            new Map(editReviewsData).set(editId, {
              ...editData,
              editReview: response.editReview,
              knownAnalyses,
              unknownAnalyses,
              processingAction: null,
            })
          );
          toast.success(`Successfully reclassified performer as ${newType}`);
        }
      } catch (error) {
        console.error('Error reclassifying performer:', error);
        toast.error('Failed to reclassify performer');
        setEditReviewsData(new Map(editReviewsData).set(editId, { ...editData, processingAction: null }));
      }
    };

    const handleMarkInvalid = async (editId: string, performerId: string, reason?: string) => {
      const editData = editReviewsData.get(editId);
      if (!editData) return;

      setEditReviewsData(new Map(editReviewsData).set(editId, { ...editData, processingAction: performerId }));

      try {
        const response = await EditReviewService.markPerformerInvalid(editId, {
          performerId,
          reason,
        });

        if (response?.editReview) {
          const { knownAnalyses, unknownAnalyses } = processEditAnalyses(response.editReview, editData.contracts);
          setEditReviewsData(
            new Map(editReviewsData).set(editId, {
              ...editData,
              editReview: response.editReview,
              knownAnalyses,
              unknownAnalyses,
              processingAction: null,
            })
          );
          toast.success('Successfully marked as invalid detection');
        }
      } catch (error) {
        console.error('Error marking performer invalid:', error);
        toast.error('Failed to mark as invalid');
        setEditReviewsData(new Map(editReviewsData).set(editId, { ...editData, processingAction: null }));
      }
    };

    const handleOutgrade = async (
      editId: string,
      performerId: string,
      contractedType: RolePerformerType,
      reason?: string
    ) => {
      const editData = editReviewsData.get(editId);
      if (!editData) return;

      setEditReviewsData(new Map(editReviewsData).set(editId, { ...editData, processingAction: performerId }));

      try {
        const response = await EditReviewService.outgradePerformer(editId, {
          performerId,
          contractedType: contractedType as unknown as TEditReviewPerformerType,
          reason,
        });

        if (response?.editReview) {
          const { knownAnalyses, unknownAnalyses } = processEditAnalyses(response.editReview, editData.contracts);
          const analysis = response.editReview.analyses?.find((a) => a.performerId === performerId);
          setEditReviewsData(
            new Map(editReviewsData).set(editId, {
              ...editData,
              editReview: response.editReview,
              knownAnalyses,
              unknownAnalyses,
              processingAction: null,
            })
          );
          toast.success(analysis?.outgraded ? 'Performer outgraded - not in this edit' : 'Outgrade removed');
        }
      } catch (error) {
        console.error('Error outgrading performer:', error);
        toast.error('Failed to outgrade performer');
        setEditReviewsData(new Map(editReviewsData).set(editId, { ...editData, processingAction: null }));
      }
    };

    const handleMergePerformers = async (editId?: string) => {
      if (!editId) return;

      const editData = editReviewsData.get(editId);
      if (!editData || editData.selectedPerformers.size < 2) return;

      setEditReviewsData(new Map(editReviewsData).set(editId, { ...editData, processingAction: 'merge' }));

      try {
        const response = await EditReviewService.mergePerformers(editId, {
          performerIds: Array.from(editData.selectedPerformers),
          newName: editData.mergeName || undefined,
        });

        if (response?.editReview) {
          const { knownAnalyses, unknownAnalyses } = processEditAnalyses(response.editReview, editData.contracts);
          setEditReviewsData(
            new Map(editReviewsData).set(editId, {
              ...editData,
              editReview: response.editReview,
              knownAnalyses,
              unknownAnalyses,
              processingAction: null,
              selectedPerformers: new Set(),
              showMergeDialog: false,
              mergeName: '',
            })
          );
          toast.success(`Successfully merged ${editData.selectedPerformers.size} performers`);
        }
      } catch (error) {
        console.error('Error merging performers:', error);
        toast.error('Failed to merge performers');
        setEditReviewsData(new Map(editReviewsData).set(editId, { ...editData, processingAction: null }));
      }
    };

    const handleIdentifyPerformer = async (
      editId: string,
      unknownPerformerId: string,
      contractedPerformerId: string
    ) => {
      const editData = editReviewsData.get(editId);
      if (!editData) return;

      setEditReviewsData(new Map(editReviewsData).set(editId, { ...editData, processingAction: unknownPerformerId }));

      try {
        const response = await EditReviewService.identifyPerformer(editId, {
          unknownPerformerId,
          contractedPerformerId,
        });

        if (response?.editReview) {
          const { knownAnalyses, unknownAnalyses } = processEditAnalyses(response.editReview, editData.contracts);
          setEditReviewsData(
            new Map(editReviewsData).set(editId, {
              ...editData,
              editReview: response.editReview,
              knownAnalyses,
              unknownAnalyses,
              processingAction: null,
            })
          );
          toast.success('Successfully identified performer');
        }
      } catch (error) {
        console.error('Error identifying performer:', error);
        toast.error('Failed to identify performer');
        setEditReviewsData(new Map(editReviewsData).set(editId, { ...editData, processingAction: null }));
      }
    };

    const togglePerformerSelection = (editId: string, performerId: string) => {
      const editData = editReviewsData.get(editId);
      if (!editData) return;

      const newSelection = new Set(editData.selectedPerformers);
      if (newSelection.has(performerId)) {
        newSelection.delete(performerId);
      } else {
        newSelection.add(performerId);
      }

      setEditReviewsData(new Map(editReviewsData).set(editId, { ...editData, selectedPerformers: newSelection }));
    };

    const updateEditData = (editId: string, updates: Partial<EditReviewData>) => {
      const editData = editReviewsData.get(editId);
      if (!editData) return;

      setEditReviewsData(new Map(editReviewsData).set(editId, { ...editData, ...updates }));
    };

    // Handle generate completion report
    const handleGenerateCompletionReport = async () => {
      try {
        const editsArray = Array.from(editReviewsData.values());
        const allCompleted = editsArray.every(
          (d) => d.editReview && d.editReview.status === EditReviewStatus.COMPLETED
        );

        if (!allCompleted || editsArray.length === 0) {
          toast.error('All edit reviews must be completed before generating the report.');
          return;
        }

        toast.info('Generating completion report...');

        const base64Pdf = await CommercialService.generateCompletionReport(commercial.id);

        // Convert base64 to blob using Buffer (more robust than atob)
        const byteArray = Uint8Array.from(Buffer.from(base64Pdf, 'base64'));
        const blob = new Blob([byteArray], { type: 'application/pdf' });

        // Open in new window
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');

        // Clean up the URL after a delay
        setTimeout(() => window.URL.revokeObjectURL(url), 5000);

        toast.success('Completion report generated!');
      } catch (error: unknown) {
        console.error('Error generating completion report:', error);
        toast.error('Failed to generate completion report.');
      }
    };

    // Handle accordion panel expand/collapse with lazy loading
    const handleAccordionChange = (activeIndexes: number[]) => {
      setActiveAccordionIndexes(activeIndexes);
      const editsArray = Array.from(editReviewsData.values());

      // Process analyses for newly expanded panels
      activeIndexes.forEach((index) => {
        const editId = editsArray[index].edit.id;
        const editData = editReviewsData.get(editId);
        if (
          editData &&
          editData.editReview &&
          editData.knownAnalyses.length === 0 &&
          editData.unknownAnalyses.length === 0
        ) {
          // First time expanding - process analyses
          const { knownAnalyses, unknownAnalyses } = processEditAnalyses(editData.editReview, editData.contracts);
          updateEditData(editId, { knownAnalyses, unknownAnalyses });
        }
      });
    };

    // Helper functions
    const getStatusTone = (status?: EditReviewStatus): StatusTone => {
      switch (status) {
        case EditReviewStatus.COMPLETED:
          return 'ok';
        case EditReviewStatus.PROCESSING:
        case EditReviewStatus.PENDING:
          return 'warning';
        case EditReviewStatus.FAILED:
          return 'danger';
        default:
          return 'neutral';
      }
    };

    const getStatusLabel = (status?: EditReviewStatus) => {
      if (!status) return 'Not Run';
      switch (status) {
        case EditReviewStatus.COMPLETED:
          return 'Complete';
        case EditReviewStatus.PROCESSING:
          return 'Processing';
        case EditReviewStatus.PENDING:
          return 'Pending';
        case EditReviewStatus.FAILED:
          return 'Error';
        default:
          return 'Unknown';
      }
    };

    const getSummaryStats = (editReview: EditReview | null): StatsData => {
      if (!editReview || !editReview.analyses) {
        return { principals: 0, extras: 0, unknown: 0 };
      }

      const analyses = editReview.analyses.filter((a) => !a.invalidDetection && !a.outgraded);
      const principals = analyses.filter((a) => a.type === RolePerformerType.OnCameraPrincipal).length;
      const extras = analyses.filter((a) => a.type === RolePerformerType.Extra).length;
      const unknown = analyses.filter((a) => !a.performerId || a.performerName).length;

      return { principals, extras, unknown };
    };

    const getWarnings = (editReview: EditReview | null) => {
      if (!editReview || !editReview.analyses) {
        return { outgraded: 0, invalid: 0, needsId: 0 };
      }

      const outgraded = editReview.analyses.filter((a) => a.outgraded).length;
      const invalid = editReview.analyses.filter((a) => a.invalidDetection).length;
      const needsId = editReview.analyses.filter((a) => !a.identifiedAs && a.performerName).length;

      return { outgraded, invalid, needsId };
    };

    const getThumbnails = (editReview: EditReview | null): string[] => {
      if (!editReview || !editReview.analyses || !editReview.framePaths) {
        return [];
      }

      const thumbnails: string[] = [];
      const analyses = editReview.analyses.filter((a) => !a.invalidDetection && a.frameAppears.length > 0);

      for (const analysis of analyses.slice(0, 4)) {
        const firstFrame = analysis.frameAppears[0];
        const framePath = editReview.framePaths.find((fp) =>
          fp.fileName.includes(`${String(firstFrame).padStart(4, '0')}.jpg`)
        );
        if (framePath?.signedUrl) {
          thumbnails.push(framePath.signedUrl);
        }
      }

      return thumbnails;
    };

    const editsArray = Array.from(editReviewsData.values());
    const completedCount = editsArray.filter(
      (d) => d.editReview && d.editReview.status === EditReviewStatus.COMPLETED
    ).length;
    const totalCount = editsArray.length;

    /** Effects **/

    // Load all edit reviews and contracts on mount
    useEffect(() => {
      const loadEditReviews = async () => {
        if (!commercial.edits || commercial.edits.length === 0) {
          setInitialLoading(false);
          return;
        }

        // Load contracts once for the commercial
        let contracts: PerformerContract[] = [];
        try {
          const c = await performerStore.listPerformerContracts({
            commercialId: commercial.id,
            relations: 'performer,role',
          });
          if (c) {
            contracts = c;
          }
        } catch (error) {
          console.error('Error loading contracts:', error);
        }

        // Initialize map with all edits
        const dataMap = new Map<string, EditReviewData>();
        commercial.edits.forEach((edit) => {
          dataMap.set(edit.id, {
            edit,
            editReview: null,
            loading: true,
            contracts,
            knownAnalyses: [],
            unknownAnalyses: [],
            processingAction: null,
            selectedPerformers: new Set(),
            showMergeDialog: false,
            mergeName: '',
            enlargedFrameUrl: null,
          });
        });
        setEditReviewsData(dataMap);

        // Fetch all edit reviews in parallel
        const promises = commercial.edits.map(async (edit) => {
          try {
            const response = await commercialStore.getEditReviewByEditId(edit.id);
            return { editId: edit.id, editReview: response?.editReview || null };
          } catch (error) {
            console.error(`Error loading edit review for edit ${edit.id}:`, error);
            return { editId: edit.id, editReview: null };
          }
        });

        const results = await Promise.all(promises);

        // Update map with results (don't process analyses yet - lazy load on expand)
        const updatedMap = new Map(dataMap);
        results.forEach(({ editId, editReview }) => {
          const existing = updatedMap.get(editId);
          if (existing) {
            updatedMap.set(editId, {
              ...existing,
              editReview,
              loading: false,
            });
          }
        });

        setEditReviewsData(updatedMap);
        setInitialLoading(false);

        // If initialEditId is provided, find its index, set it as active, and process its analyses
        if (initialEditId) {
          const editIndex = commercial.edits?.findIndex((edit) => edit.id === initialEditId);
          if (editIndex !== undefined && editIndex !== -1) {
            setActiveAccordionIndexes([editIndex]);

            // Manually process analyses for the initial edit
            const editData = updatedMap.get(initialEditId);
            if (
              editData &&
              editData.editReview &&
              editData.knownAnalyses.length === 0 &&
              editData.unknownAnalyses.length === 0
            ) {
              const { knownAnalyses, unknownAnalyses } = processEditAnalyses(editData.editReview, editData.contracts);
              const finalMap = new Map(updatedMap);
              finalMap.set(initialEditId, { ...editData, knownAnalyses, unknownAnalyses });
              setEditReviewsData(finalMap);
            }
          }
        }
      };

      loadEditReviews();
    }, [commercial.edits, commercialStore, performerStore, initialEditId]);

    // Listen for WebSocket messages about EditReview completion
    useEffect(() => {
      if (!lastJsonMessage) return;

      const message = lastJsonMessage as ChatMessage;

      // Check if this is an edit review completion notification
      if (message.source_entity_name === 'edits' && message.source_entity_id) {
        // Parse the entity ID format: "commercialId/editId"
        const parts = message.source_entity_id.split('/');
        if (parts.length === 2) {
          const [notificationCommercialId, notificationEditId] = parts;

          // Check if this notification is for the current commercial
          if (notificationCommercialId === commercial.id) {
            console.log(`Received EditReview completion notification for edit ${notificationEditId}`);

            // Fetch the updated EditReview data
            commercialStore
              .getEditReviewByEditId(notificationEditId)
              .then((response) => {
                if (response?.editReview) {
                  const updatedEditReview = response.editReview; // Extract to const for type safety

                  setEditReviewsData((prevData) => {
                    const existingData = prevData.get(notificationEditId);

                    if (!existingData) {
                      return prevData; // Edit not in current modal
                    }

                    const updatedMap = new Map(prevData);

                    // Check if the accordion for this edit is expanded
                    const editIndex = commercial.edits?.findIndex((e) => e.id === notificationEditId) ?? -1;
                    const isExpanded = activeAccordionIndexes.includes(editIndex);

                    let knownAnalyses = existingData.knownAnalyses;
                    let unknownAnalyses = existingData.unknownAnalyses;

                    // Process analyses if expanded OR if there are no analyses yet
                    if (isExpanded || (knownAnalyses.length === 0 && unknownAnalyses.length === 0)) {
                      const processed = processEditAnalyses(updatedEditReview, existingData.contracts);
                      knownAnalyses = processed.knownAnalyses;
                      unknownAnalyses = processed.unknownAnalyses;
                    }

                    updatedMap.set(notificationEditId, {
                      ...existingData,
                      editReview: updatedEditReview,
                      loading: false,
                      knownAnalyses,
                      unknownAnalyses,
                    });

                    console.log(
                      `Updated EditReview data for edit ${notificationEditId}, status: ${updatedEditReview.status}`
                    );
                    return updatedMap;
                  });
                }
              })
              .catch((error) => {
                console.error(`Error fetching updated EditReview for edit ${notificationEditId}:`, error);
              });
          }
        }
      }
    }, [
      lastJsonMessage,
      commercial.id,
      commercial.edits,
      activeAccordionIndexes,
      commercialStore,
      processEditAnalyses,
    ]);

    /** Render **/

    const Loader = ({ spinnerSize = 'large', ...props }: BoxProps & { spinnerSize?: SpinnerProps['size'] }) => (
      <Box align="center" justify="center" pad="large" {...props}>
        <Spinner size={spinnerSize} />
        <Text margin={{ top: 'small' }}>Loading edit reviews...</Text>
      </Box>
    );

    const CommercialTitle = ({ data, ...props }: BoxProps & { data: EditReviewData }) => (
      <Box direction="row" gap="small" align="center" wrap={false} {...props}>
        <Box flex={false}>
          <Heading level={4} margin="none">
            {data.edit.title}
          </Heading>
        </Box>
        <StatusPill label={getStatusLabel(data.editReview?.status)} tone={getStatusTone(data.editReview?.status)} />
      </Box>
    );

    const CommercialStats = ({ stats, ...props }: BoxProps & { stats: StatsData }) => (
      <Box direction="row" gap="medium" {...props}>
        <Text size="small">
          <strong>{stats.principals}</strong> principals
        </Text>
        <Text size="small">
          <strong>{stats.extras}</strong> extras
        </Text>
        <Text size="small">
          <strong>{stats.unknown}</strong> unknown
        </Text>
      </Box>
    );

    const CommercialThumbnails = ({ thumbnails, ...props }: BoxProps & { thumbnails: string[] }) => (
      <Box direction="row" gap="xsmall" {...props}>
        {thumbnails.map((url, i) => (
          <Box key={i} width="60px" height="60px" overflow="hidden" round="xsmall">
            <Image src={url} fit="cover" />
          </Box>
        ))}
      </Box>
    );

    const ErrorMessage = ({ data, ...props }: BoxProps & { data: EditReviewData }) => (
      <Box {...props}>
        <Text size="small" color="status-critical">
          Error: {data.editReview?.error_message}
        </Text>
      </Box>
    );

    const MissingAssetWarning = (props?: BoxProps) => (
      <Box
        direction="row"
        gap="xxsmall"
        align="center"
        background="status-warning"
        pad={{ horizontal: 'small', vertical: 'xxsmall' }}
        round="xsmall"
        {...props}
      >
        <Text size="xsmall" color="white">
          ⚠️ No video asset - upload required to run review
        </Text>
      </Box>
    );

    const CommercialWarnings = ({ warnings, ...props }: BoxProps & { warnings: WarningsData }) => (
      <Box direction="row" gap="small" {...props}>
        {warnings.outgraded > 0 && (
          <Box
            direction="row"
            gap="xxsmall"
            align="center"
            background="status-critical"
            pad={{ horizontal: 'small', vertical: 'xxsmall' }}
            round="xsmall"
          >
            <StatusWarning size="small" color="white" />
            <Text size="xsmall" color="white">
              {warnings.outgraded} outgraded
            </Text>
          </Box>
        )}
        {warnings.invalid > 0 && (
          <Box
            direction="row"
            gap="xxsmall"
            align="center"
            background="status-warning"
            pad={{ horizontal: 'small', vertical: 'xxsmall' }}
            round="xsmall"
          >
            <Alert size="small" color="white" />
            <Text size="xsmall" color="white">
              {warnings.invalid} invalid
            </Text>
          </Box>
        )}
        {warnings.needsId > 0 && (
          <Box
            direction="row"
            gap="xxsmall"
            align="center"
            background="accent-1"
            pad={{ horizontal: 'small', vertical: 'xxsmall' }}
            round="xsmall"
          >
            <Text size="xsmall" color="white">
              ❓ {warnings.needsId} need ID
            </Text>
          </Box>
        )}
      </Box>
    );

    const ActionButton = ({ data, ...props }: BoxProps & { data: EditReviewData }) => (
      <Box
        flex={false}
        margin={{ left: 'small' }}
        onClick={(e) => {
          e.stopPropagation();
          if (
            !data.loading &&
            !batchProgress &&
            data.edit.asset_id &&
            data.editReview?.status !== EditReviewStatus.PENDING &&
            data.editReview?.status !== EditReviewStatus.PROCESSING
          ) {
            handleRunReview(data.edit.id);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            if (
              !data.loading &&
              !batchProgress &&
              data.edit.asset_id &&
              data.editReview?.status !== EditReviewStatus.PENDING &&
              data.editReview?.status !== EditReviewStatus.PROCESSING
            ) {
              handleRunReview(data.edit.id);
            }
          }
        }}
        pad={{ horizontal: 'small', vertical: 'xsmall' }}
        round="xsmall"
        background={
          data.loading ||
          !!batchProgress ||
          !data.edit.asset_id ||
          data.editReview?.status === EditReviewStatus.PENDING ||
          data.editReview?.status === EditReviewStatus.PROCESSING
            ? 'light-3'
            : 'white'
        }
        border={{ color: 'brand', size: 'small' }}
        style={{
          cursor:
            data.loading ||
            !!batchProgress ||
            !data.edit.asset_id ||
            data.editReview?.status === EditReviewStatus.PENDING ||
            data.editReview?.status === EditReviewStatus.PROCESSING
              ? 'not-allowed'
              : 'pointer',
          opacity:
            data.loading ||
            !!batchProgress ||
            !data.edit.asset_id ||
            data.editReview?.status === EditReviewStatus.PENDING ||
            data.editReview?.status === EditReviewStatus.PROCESSING
              ? 0.5
              : 1,
          userSelect: 'none',
        }}
        tabIndex={0}
        role="button"
        aria-label={
          data.editReview?.status === EditReviewStatus.PROCESSING
            ? 'Processing...'
            : data.editReview?.status === EditReviewStatus.PENDING
              ? 'Pending...'
              : data.editReview
                ? 'Re-run edit review'
                : 'Run edit review'
        }
        aria-disabled={
          data.loading ||
          !!batchProgress ||
          !data.edit.asset_id ||
          data.editReview?.status === EditReviewStatus.PENDING ||
          data.editReview?.status === EditReviewStatus.PROCESSING
        }
        title={
          !data.edit.asset_id
            ? 'Upload a video asset first'
            : data.editReview?.status === EditReviewStatus.PENDING
              ? 'Review is queued for processing'
              : data.editReview?.status === EditReviewStatus.PROCESSING
                ? 'Review is currently being processed'
                : undefined
        }
        {...props}
      >
        <Box direction="row" gap="xsmall" align="center">
          {data.editReview?.status === EditReviewStatus.PROCESSING ||
          data.editReview?.status === EditReviewStatus.PENDING ? (
              <Spinner size="small" />
            ) : data.editReview ? (
              <Update size="small" />
            ) : (
              <Magic size="small" />
            )}
          <Text size="small">
            {data.editReview?.status === EditReviewStatus.PROCESSING
              ? 'Processing...'
              : data.editReview?.status === EditReviewStatus.PENDING
                ? 'Pending...'
                : data.editReview
                  ? 'Re-run'
                  : 'Run Review'}
          </Text>
        </Box>
      </Box>
    );

    return (
      <Layer
        onEsc={() => setShowCommercialEditReviewModal(false)}
        onClickOutside={() => setShowCommercialEditReviewModal(false)}
        position="center"
        modal
        background="transparent"
      >
        <ModalCard
          onClose={() => setShowCommercialEditReviewModal(false)}
          title={`Edit Reviews: ${commercial.title}`}
          noLayerComponent
          cardProps={{ width: '845px', height: '60vh' }}
          headerSubtext={`${completedCount} of ${totalCount} complete`}
          bodyProps={{ gap: 'medium' }}
        >
          <CardHeader justify="end" direction="row" gap="small" pad="small" align="center">
            {batchProgress && (
              <Box direction="row" gap="small" align="center" flex>
                <Spinner size="small" />
                <Text size="small">
                  Processing {batchProgress.current}/{batchProgress.total}
                </Text>
              </Box>
            )}

            <Button
              label="Generate Final Cast"
              icon={<Checkmark />}
              onClick={handleGenerateCompletionReport}
              disabled={completedCount !== totalCount || totalCount === 0 || initialLoading}
              primary
            />
            <Button
              label="Run All Reviews"
              icon={<Magic />}
              onClick={handleRunAllReviews}
              disabled={!!batchProgress || initialLoading}
              primary
            />
          </CardHeader>

          <CardBody gap="large">
            {initialLoading && <Loader />}
            <Accordion multiple animate={false} onActive={handleAccordionChange} activeIndex={activeAccordionIndexes}>
              {editsArray.map((data) => {
                const stats = getSummaryStats(data.editReview);
                const warnings = getWarnings(data.editReview);
                const thumbnails = getThumbnails(data.editReview);

                return (
                  <AccordionPanel
                    key={data.edit.id}
                    label={
                      <Box
                        direction="row"
                        justify="between"
                        align="start"
                        fill="horizontal"
                        pad="small"
                        margin={{ bottom: 'small' }}
                        background="light-2"
                        round="xsmall"
                        border={{ color: 'light-4', size: 'small' }}
                      >
                        <Box flex="grow" gap="small" style={{ minWidth: 0 }}>
                          <CommercialTitle data={data} />

                          <Text size="small" color="text-weak">
                            {data.edit.type} • {data.edit.duration}s
                          </Text>

                          {/* Stats */}
                          {data.editReview?.status === EditReviewStatus.COMPLETED && <CommercialStats stats={stats} />}

                          {/* Thumbnails */}
                          {thumbnails.length > 0 && <CommercialThumbnails thumbnails={thumbnails} />}

                          {/* Warnings */}
                          {data.editReview?.status === EditReviewStatus.COMPLETED &&
                            (warnings.outgraded > 0 || warnings.invalid > 0 || warnings.needsId > 0) && (
                            <CommercialWarnings warnings={warnings} />
                          )}

                          {/* Error message */}
                          {data.editReview?.status === EditReviewStatus.FAILED && data.editReview?.error_message && (
                            <ErrorMessage data={data} />
                          )}

                          {/* Missing asset warning */}
                          {!data.edit.asset_id && <MissingAssetWarning />}
                        </Box>

                        {/* Action button in header - using div to avoid nested button warning */}
                        <ActionButton data={data} />
                      </Box>
                    }
                  >
                    {/* Detailed view inside accordion */}
                    {data.loading && !data.editReview && <Loader />}

                    {data.editReview && (
                      <Box gap="medium" margin={{ top: 'small' }}>
                        {/* Contracted Performers Section */}
                        {data.knownAnalyses.length > 0 && (
                          <ContractedPerformersPanel
                            knownAnalyses={data.knownAnalyses}
                            editReview={data.editReview}
                            processingAction={data.processingAction}
                            setEnlargedFrameUrl={(val) =>
                              updateEditData(data.edit.id, { enlargedFrameUrl: val ?? null })
                            }
                            handleOutgrade={handleOutgrade}
                            handleReclassify={handleReclassify}
                            handleMarkInvalid={handleMarkInvalid}
                          />
                        )}

                        {/* Unknown Performers Section */}
                        {data.unknownAnalyses.length > 0 && (
                          <UnknownPerformersPanel
                            unknownAnalyses={data.unknownAnalyses}
                            contracts={data.contracts}
                            selectedPerformers={data.selectedPerformers}
                            processingAction={data.processingAction}
                            setEnlargedFrameUrl={(val) =>
                              updateEditData(data.edit.id, { enlargedFrameUrl: val ?? null })
                            }
                            setShowMergeDialog={(val) => updateEditData(data.edit.id, { showMergeDialog: val })}
                            togglePerformerSelection={togglePerformerSelection}
                            handleIdentifyPerformer={handleIdentifyPerformer}
                            handleReclassify={handleReclassify}
                            handleMarkInvalid={handleMarkInvalid}
                          />
                        )}
                      </Box>
                    )}

                    {/* Enlarged frame popup */}
                    {data.enlargedFrameUrl && (
                      <EnlargedFrameModal
                        enlargedFrameUrl={data.enlargedFrameUrl}
                        setEnlargedFrameUrl={(val) => updateEditData(data.edit.id, { enlargedFrameUrl: val ?? null })}
                      />
                    )}

                    {/* Merge dialog */}
                    {data.showMergeDialog && (
                      <MergeDialogModal
                        unknownAnalyses={data.unknownAnalyses}
                        selectedPerformers={data.selectedPerformers}
                        processingAction={data.processingAction}
                        enlargedFrameUrl={data.enlargedFrameUrl ?? undefined}
                        mergeName={data.mergeName}
                        setMergeName={(val) => updateEditData(data.edit.id, { mergeName: val || undefined })}
                        setShowMergeDialog={(val) => updateEditData(data.edit.id, { showMergeDialog: val })}
                        handleMergePerformers={handleMergePerformers}
                      />
                    )}
                  </AccordionPanel>
                );
              })}
            </Accordion>
          </CardBody>
        </ModalCard>
      </Layer>
    );
  }
);
